import React from 'react';
import { Mail, Heart } from 'lucide-react';
import { ErrorBoundary } from '../ErrorBoundary';
import { getAnimationCSS } from '../../utils/customisation';

/* ─── Countdown, profile music, email signup and tip jar widgets ─── */
export const ProfileWidgets = ({
  profile, ts, accent, anim, isLeft, username, countdown,
  subEmail, setSubEmail, subbing, subDone, subMsg, handleSubscribe,
  formatTip, tipSymbol, handleTip,
}) => {
  return (
    <>
        {/* Countdown Timer */}
        {countdown && profile.countdown_date && (
          <div className="mb-6 p-4 rounded-xl text-center" style={getAnimationCSS(anim, 1)}
            data-testid="countdown-widget">
            <div style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }} className="rounded-xl p-5">
              {profile.countdown_label && (
                <p className="text-sm font-semibold mb-3" style={{ color: accent }}>{profile.countdown_label}</p>
              )}
              <div className="flex justify-center gap-3">
                {[
                  { val: countdown.days, label: 'Days' },
                  { val: countdown.hours, label: 'Hrs' },
                  { val: countdown.minutes, label: 'Min' },
                  { val: countdown.seconds, label: 'Sec' }
                ].map(({ val, label }) => (
                  <div key={label} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold tabular-nums" style={{ color: ts['--theme-text'] }}>{String(val).padStart(2, '0')}</div>
                    <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: ts['--theme-text-muted'] }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Profile Music Player */}
        {profile.profile_music_url && (
          <div className="mb-6" style={getAnimationCSS(anim, 1)} data-testid="music-player">
            <ErrorBoundary fallback={null}>
              {profile.profile_music_url.includes('spotify.com') && /\/(track|album|playlist|episode)\//.test(profile.profile_music_url) ? (
                <iframe src={`${profile.profile_music_url.replace('open.spotify.com/', 'open.spotify.com/embed/')}${profile.profile_music_url.includes('?') ? '&' : '?'}utm_source=generator&theme=0`}
                  width="100%" height="80" frameBorder="0" allow="encrypted-media" loading="lazy"
                  className="rounded-xl" title="Profile music" />
              ) : profile.profile_music_url.includes('soundcloud.com') ? (
                <iframe width="100%" height="120" frameBorder="0"
                  src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(profile.profile_music_url)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
                  className="rounded-xl" title="Profile music" loading="lazy" />
              ) : null}
            </ErrorBoundary>
          </div>
        )}

        {/* Email Signup Widget */}
        {profile.email_signup_enabled && (
          <div className="mb-6 p-4 rounded-xl" style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}`, ...getAnimationCSS(anim, 1) }}
            data-testid="email-signup-widget">
            <div className={`${isLeft ? 'text-left' : 'text-center'}`}>
              <Mail className="w-5 h-5 mx-auto mb-2" style={{ color: accent }} />
              <p className="text-sm font-semibold mb-1" style={{ color: ts['--theme-text'] }}>{profile.email_signup_title || 'Stay Connected'}</p>
              <p className="text-xs mb-3" style={{ color: ts['--theme-text-muted'] }}>{profile.email_signup_subtitle || 'Get updates straight to your inbox'}</p>
              {subDone ? (
                <p className="text-sm font-medium" style={{ color: accent }} data-testid="email-signup-done-msg">
                  {subMsg || 'Almost done — check your inbox to confirm your subscription.'}
                </p>
              ) : (
                <div className="flex gap-2 max-w-sm mx-auto">
                  <input type="email" value={subEmail} onChange={(e) => setSubEmail(e.target.value)}
                    placeholder="your@email.com" className="flex-1 px-3 py-2 text-sm rounded-lg border bg-transparent outline-none"
                    style={{ borderColor: ts['--theme-card-border'], color: ts['--theme-text'] }}
                    data-testid="email-signup-input" />
                  <button onClick={handleSubscribe} disabled={subbing}
                    className="px-4 py-2 text-sm font-medium rounded-lg transition-opacity hover:opacity-80"
                    style={{ background: accent, color: ts['--theme-accent-text'] || '#fff' }}
                    data-testid="email-signup-btn">
                    {subbing ? '...' : (profile.email_signup_button || 'Subscribe')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tip Jar Widget */}
        {profile.tip_jar_enabled && profile.tip_jar_paypal && (
          <div className="mb-6 p-4 rounded-xl" style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}`, ...getAnimationCSS(anim, 1) }}
            data-testid="tip-jar-widget">
            <div className={`${isLeft ? 'text-left' : 'text-center'}`}>
              <Heart className="w-5 h-5 mx-auto mb-2" style={{ color: '#EC4899' }} />
              <p className="text-sm font-semibold mb-1" style={{ color: ts['--theme-text'] }}>{profile.tip_jar_title || 'Tip Jar'}</p>
              <p className="text-xs mb-3" style={{ color: ts['--theme-text-muted'] }}>{profile.tip_jar_subtitle || `Support ${profile.name || username} with a tip`}</p>

              {/* Balance + goal progress (only when explicitly enabled by owner) */}
              {profile.tip_jar_show_balance && (
                <div className="mb-4 max-w-sm mx-auto" data-testid="tip-jar-progress">
                  {profile.tip_jar_goal && profile.tip_jar_goal > 0 ? (
                    <>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <span className="text-lg font-bold tabular-nums" style={{ color: ts['--theme-text'] }} data-testid="tip-jar-balance">
                          {formatTip(profile.tip_jar_balance || 0)}
                        </span>
                        <span className="text-xs tabular-nums" style={{ color: ts['--theme-text-muted'] }} data-testid="tip-jar-goal">
                          of {formatTip(profile.tip_jar_goal)}
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: `${ts['--theme-card-border']}` }}>
                        <div className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${Math.min(100, ((profile.tip_jar_balance || 0) / profile.tip_jar_goal) * 100)}%`,
                            background: 'linear-gradient(90deg, #EC4899, #F472B6)'
                          }} />
                      </div>
                      <p className="text-[11px] mt-1.5 tabular-nums" style={{ color: ts['--theme-text-muted'] }}>
                        {Math.round(((profile.tip_jar_balance || 0) / profile.tip_jar_goal) * 100)}% funded
                      </p>
                    </>
                  ) : (
                    <div className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-full" style={{ background: '#EC489918', border: '1px solid #EC489940' }}>
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: ts['--theme-text-muted'] }}>raised</span>
                      <span className="text-base font-bold tabular-nums" style={{ color: '#EC4899' }} data-testid="tip-jar-balance">
                        {formatTip(profile.tip_jar_balance || 0)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className={`flex flex-wrap gap-2 ${isLeft ? 'justify-start' : 'justify-center'}`}>
                {[1, 3, 5, 10].map(amt => (
                  <button key={amt} onClick={() => handleTip(amt)}
                    className="px-4 py-2 text-sm font-medium rounded-lg transition-all hover:opacity-80 hover:scale-105"
                    style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}40` }}
                    data-testid={`tip-${amt}`}>
                    {tipSymbol}{amt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
    </>
  );
};
